/**
 * Scalar schemas.
 *
 * Branded types are produced with `z.custom` plus an explicit predicate rather than Zod's own
 * `.brand()`. Zod brands with its own symbol; `@orbit-mind/shared-types` brands with a different
 * one, and a schema that produced `z.BRAND<'MapId'>` would not be assignable to `MapId` — the
 * two systems would look compatible and silently disagree at every call site.
 */
import * as z from 'zod';
/**
 * Applies a brand to an already-validated schema.
 *
 * The cast is safe precisely because `check` ran first. Keeping the two in one helper means no
 * schema can brand a value it did not validate — the mistake this exists to prevent.
 */
function branded(check) {
    return check.transform((value) => value);
}
const HEX_COLOR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const SHA256 = /^[0-9a-f]{64}$/;
const LANGUAGE_TAG = /^[a-zA-Z]{2,3}(?:-[a-zA-Z0-9]{2,8})*$/;
/**
 * Milliseconds since the epoch.
 *
 * Rejects negatives and non-integers. A timestamp arriving as a float is a unit error — seconds
 * where milliseconds were expected — and silently accepting it dates a document to 1970.
 */
export const timestampSchema = branded(z.number().int().nonnegative());
export const millisSchema = branded(z.number().int().nonnegative());
export const iso8601Schema = branded(z.iso.datetime({ offset: true }));
export const logicalClockSchema = branded(z.number().int().nonnegative());
export const schemaVersionSchema = branded(z.number().int().positive());
export const sha256Schema = branded(z.string().regex(SHA256, 'expected 64 lowercase hex characters'));
export const hexColorSchema = branded(z.string().regex(HEX_COLOR, 'expected #rgb, #rrggbb or #rrggbbaa'));
export const nonEmptyStringSchema = branded(z.string().refine((value) => value.trim().length > 0, 'must not be blank'));
export const languageTagSchema = branded(z.string().regex(LANGUAGE_TAG, 'expected a BCP 47 tag such as pt-BR'));
export const byteSizeSchema = branded(z.number().int().nonnegative());
/**
 * A coordinate.
 *
 * `z.number()` already rejects `Infinity`, `-Infinity` and `NaN` in Zod v4 — an explicit
 * `.finite()` is a deprecated no-op. The alias is kept because the guarantee is the reason these
 * fields are validated at all: those three values survive `JSON.parse` of a hand-edited file and
 * then propagate through every layout calculation, producing a map that renders as nothing with
 * no error anywhere. Naming it says which property the coordinate depends on.
 */
export const finiteNumber = z.number();
export const pointSchema = z.object({
    x: finiteNumber,
    y: finiteNumber,
});
export const sizeSchema = z.object({
    width: finiteNumber.nonnegative(),
    height: finiteNumber.nonnegative(),
});
export const viewportSchema = z.object({
    x: finiteNumber,
    y: finiteNumber,
    // A zoom of zero collapses the projection matrix and divides by zero downstream.
    zoom: finiteNumber.positive(),
    rotation: finiteNumber.optional(),
});
//# sourceMappingURL=primitives.js.map